// dialog.js — promise-based imperative API for the in-app dialog system.
//
// Drop-in replacement for window.confirm / window.alert with consistent
// styling, focus management, keyboard handling, and the same async shape:
//
//   import { dialog } from '@/lib/dialog'
//   if (!await dialog.confirm({ title: 'Delete?', body: `Delete "${name}"?` })) return
//   await dialog.alert({ title: 'Error', body: e.message, variant: 'error' })
//
// The <DialogHost /> component subscribes here and renders the active
// dialog. Mount DialogHost once near the root of the React tree.

let _seq = 0
const _subs = new Set()
let _stack = [] // multiple dialogs can stack; top one is rendered

function notify() {
  for (const fn of _subs) fn(_stack)
}

function push(payload) {
  return new Promise((resolve) => {
    _stack = [..._stack, { id: ++_seq, payload, resolve }]
    notify()
  })
}

function resolveTop(value) {
  if (_stack.length === 0) return
  const top = _stack[_stack.length - 1]
  _stack = _stack.slice(0, -1)
  notify()
  top.resolve(value)
}

export const dialog = {
  /**
   * Show a confirmation dialog. Resolves to true if the user confirmed,
   * false otherwise (cancel button, Escape, or backdrop click when dismissible).
   *
   * @param {object} opts
   * @param {string} opts.title
   * @param {string|React.ReactNode} opts.body
   * @param {string} [opts.confirmLabel="OK"]
   * @param {string} [opts.cancelLabel="Cancel"]
   * @param {boolean} [opts.danger=false] — render confirm button in red
   * @param {boolean} [opts.dismissible=true] — Escape / backdrop close as cancel
   * @param {{confirmId?: string, testId?: string, holdMs?: number}} [opts.holdToConfirm]
   *   — when set, the confirm button is a HoldToConfirm control: it resolves
   *   true only after a completed hold, and Enter no longer confirms (a single
   *   keystroke must not be able to complete a held confirmation). Cancel,
   *   Escape, and backdrop dismissal are unchanged.
   * @param {string} [opts.confirmTestId] — `data-testid` on the plain confirm
   *   button, so a spec can press THIS dialog's confirm rather than whichever
   *   button says "Delete".
   * @returns {Promise<boolean>}
   */
  confirm(opts) {
    return push({ kind: 'confirm', ...opts })
  },

  /**
   * Show an alert dialog. Resolves when the user dismisses it.
   *
   * @param {object} opts
   * @param {string} opts.title
   * @param {string|React.ReactNode} opts.body
   * @param {string} [opts.okLabel="OK"]
   * @param {'info'|'error'|'warning'} [opts.variant='info']
   * @returns {Promise<void>}
   */
  alert(opts) {
    return push({ kind: 'alert', ...opts })
  },

  /**
   * Show a picker dialog — a list of options the user chooses from.
   * Resolves to the chosen option's `value`, or null if dismissed
   * (Escape / backdrop click when dismissible).
   *
   * @param {object} opts
   * @param {string} opts.title
   * @param {string|React.ReactNode} [opts.body]
   * @param {Array<{label: string, value: any, danger?: boolean}>} opts.options
   * @param {boolean} [opts.dismissible=true]
   * @returns {Promise<any|null>}
   */
  choose(opts) {
    return push({ kind: 'choose', ...opts })
  },

  /**
   * Show a single-line text-input dialog (the "Make a copy" pattern).
   * Resolves to the trimmed entered string on confirm, or null if dismissed
   * (Cancel, Escape, or backdrop click when dismissible).
   *
   * @param {object} opts
   * @param {string} opts.title
   * @param {string|React.ReactNode} [opts.body]
   * @param {string} [opts.label]
   * @param {string} [opts.initialValue='']
   * @param {boolean} [opts.selectAll=false] — pre-select initialValue on mount
   * @param {string} [opts.confirmLabel="OK"]
   * @param {string} [opts.cancelLabel="Cancel"]
   * @param {string} [opts.placeholder]
   * @param {number} [opts.maxLength]
   * @param {(value: string) => string|null} [opts.validate] — returns an
   *   inline error string to show, or null when the value is OK.
   * @param {boolean} [opts.dismissible=true]
   * @returns {Promise<string|null>}
   */
  prompt(opts) {
    return push({ kind: 'prompt', ...opts })
  },
}

// Internal — used by DialogHost.
export function _subscribe(fn) {
  _subs.add(fn)
  fn(_stack)
  return () => _subs.delete(fn)
}

export function _resolve(value) {
  resolveTop(value)
}
